import { useState, useCallback, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";

// Cache storage with expiration
class SuggestionsCache {
	constructor(maxSize = 50, expirationMs = 24 * 60 * 60 * 1000) {
		// 24 hours
		this.cache = new Map();
		this.maxSize = maxSize;
		this.expirationMs = expirationMs;
	}

	set(email, data) {
		// Remove oldest entry if cache is full
		if (this.cache.size >= this.maxSize) {
			const oldestKey = this.cache.keys().next().value;
			this.cache.delete(oldestKey);
		}

		this.cache.set(email.toLowerCase(), {
			data,
			timestamp: Date.now(),
			useCount: 0,
		});
	}

	get(email) {
		const entry = this.cache.get(email.toLowerCase());
		if (!entry) return null;

		// Check if entry has expired
		if (Date.now() - entry.timestamp > this.expirationMs) {
			this.cache.delete(email.toLowerCase());
			return null;
		}

		// Increment use count
		entry.useCount++;
		return entry.data;
	}

	getRecent(limit = 5) {
		return Array.from(this.cache.entries())
			.filter(([_, entry]) => Date.now() - entry.timestamp <= this.expirationMs)
			.sort((a, b) => b[1].useCount - a[1].useCount)
			.slice(0, limit)
			.map(([email, entry]) => entry.data);
	}
}

// Create a singleton cache instance
const suggestionsCache = new SuggestionsCache();

export const useCalendarSuggestions = () => {
	const [suggestions, setSuggestions] = useState([]);
	const [recentSelections, setRecentSelections] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const { tokens, isAuthenticated } = useAuth();

	// Load recent selections on mount
	useEffect(() => {
		setRecentSelections(suggestionsCache.getRecent());
	}, []);

	const addToCache = useCallback((suggestion) => {
		// Only add the single suggestion to cache
		suggestionsCache.set(suggestion.email, suggestion);
		// Update recent selections
		setRecentSelections(suggestionsCache.getRecent());
	}, []);

	const fetchSuggestions = useCallback(
		async (query) => {
			if (!isAuthenticated || !tokens || !query?.trim()) {
				setSuggestions([]);
				return;
			}

			setLoading(true);
			setError(null);

			// Get cached results first
			const cachedResults = [];
			const queryLower = query.toLowerCase();
			const recentOnes = suggestionsCache.getRecent();

			for (const recent of recentOnes) {
				if (
					recent.email.toLowerCase().includes(queryLower) ||
					recent.displayName.toLowerCase().includes(queryLower)
				) {
					cachedResults.push(recent);
				}
			}

			try {
				const response = await fetch(
					`https://api.my-cron.com/api/calendar/users/suggest?${new URLSearchParams(
						{
							query,
							tokens: JSON.stringify(tokens),
						}
					)}`,
					{
						method: "GET",
						headers: {
							"Content-Type": "application/json",
						},
					}
				);

				if (!response.ok) {
					throw new Error(`Failed to fetch suggestions: ${response.status}`);
				}

				const { suggestions: apiSuggestions } = await response.json();

				// Just set the suggestions for display, don't cache them yet
				// They'll only be cached when specifically selected
				setSuggestions(apiSuggestions);
			} catch (err) {
				console.error("Error fetching suggestions:", err);
				setError(err.message);
				setSuggestions(cachedResults);
			} finally {
				setLoading(false);
			}
		},
		[tokens, isAuthenticated]
	);

	return {
		suggestions,
		recentSelections,
		loading,
		error,
		fetchSuggestions,
		addToCache,
	};
};
